import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FirebaseService } from '../services/firebaseService';
import AdminEclairagePublicSubcategories from './AdminEclairagePublicSubcategories';

interface AdminEclairagePublicProps {
  t: any;
  isRTL: boolean;
  formData: any;
  onInputChange: (key: string, value: any) => void;
}

const Container = styled.div<{ $isRTL: boolean }>`
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const SectionTitle = styled.h3<{ $isRTL: boolean }>`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 1.5rem 0;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const TabContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  border-bottom: 2px solid #e5e7eb;
`;

const Tab = styled.button<{ $isActive: boolean; $isRTL: boolean }>`
  padding: 0.75rem 1.5rem;
  border: none;
  background: ${props => props.$isActive ? '#dc2626' : 'transparent'};
  color: ${props => props.$isActive ? 'white' : '#374151'};
  border-radius: 8px 8px 0 0;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  &:hover {
    background: ${props => props.$isActive ? '#b91c1c' : '#f3f4f6'};
  }
`;

const FormGroup = styled.div<{ $isRTL: boolean }>`
  margin-bottom: 1.5rem;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const Label = styled.label<{ $isRTL: boolean }>`
  display: block;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const Input = styled.input<{ $isRTL: boolean }>`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  &:focus {
    outline: none;
    border-color: #dc2626;
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
  }
`;

const TextArea = styled.textarea<{ $isRTL: boolean }>`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  &:focus {
    outline: none;
    border-color: #dc2626;
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
  }
`;

const Select = styled.select<{ $isRTL: boolean }>`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  &:focus {
    outline: none;
    border-color: #dc2626;
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
  }
`;

const Button = styled.button<{ $isRTL: boolean; $variant?: 'primary' | 'secondary' }>`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  ${props => props.$variant === 'primary' ? `
    background: #dc2626;
    color: white;
    
    &:hover {
      background: #b91c1c;
    }
  ` : `
    background: #f3f4f6;
    color: #374151;
    border: 1px solid #d1d5db;
    
    &:hover {
      background: #e5e7eb;
    }
  `}
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const EnsemblesList = styled.div`
  margin-top: 2rem;
`;

const EnsembleCard = styled.div<{ $isRTL: boolean }>`
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const EnsembleHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const EnsembleTitle = styled.h4<{ $isRTL: boolean }>`
  font-size: 1.2rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const EnsembleActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled.button<{ $variant: 'edit' | 'delete' }>`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  ${props => props.$variant === 'edit' ? `
    background: #3b82f6;
    color: white;
    
    &:hover {
      background: #2563eb;
    }
  ` : `
    background: #ef4444;
    color: white;
    
    &:hover {
      background: #dc2626;
    }
  `}
`;

const ProductsList = styled.div<{ $isRTL: boolean }>`
  margin-top: 1rem;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const ProductItem = styled.div<{ $isRTL: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const ProductInput = styled.input<{ $isRTL: boolean }>`
  flex: 1;
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.9rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const RemoveButton = styled.button`
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  font-size: 0.8rem;
  cursor: pointer;
  
  &:hover {
    background: #dc2626;
  }
`;

const AddButton = styled.button<{ $isRTL: boolean }>`
  background: #10b981;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  cursor: pointer;
  margin-top: 0.5rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  &:hover {
    background: #059669;
  }
`;

const SpecsSection = styled.div<{ $isRTL: boolean }>`
  margin-top: 1rem;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const SpecRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  align-items: center;
`;

const SpecInput = styled.input<{ $isRTL: boolean }>`
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.9rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
  font-size: 1.1rem;
  color: #64748b;
`;

interface Ensemble {
  id: string;
  title: string;
  description: string;
  mainImage: string;
  additionalImages: string[];
  products: string[];
  compositionImage: string;
  brochurePDF: string;
  catalogPDF: string;
  technicalSpecs: { [key: string]: string };
  subcategoryId: string;
  createdAt: Date;
  updatedAt: Date;
}

interface Subcategory {
  id: string;
  name: string;
  description: string;
  image: string;
  createdAt: Date;
  updatedAt: Date;
}

const AdminEclairagePublic: React.FC<AdminEclairagePublicProps> = ({ t, isRTL, formData, onInputChange }) => {
  const [localFormData, setLocalFormData] = useState<any>({ newSubcategory: {}, newEnsemble: {} });
  
  const handleLocalInputChange = (key: string, value: any) => {
    setLocalFormData((prev: any) => ({
      ...prev,
      [key]: value
    }));
  };
  const [activeTab, setActiveTab] = useState<'subcategories' | 'ensembles'>('subcategories');
  const [ensembles, setEnsembles] = useState<Ensemble[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingEnsemble, setEditingEnsemble] = useState<Ensemble | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [ensemblesData, subcategoriesData] = await Promise.all([
        FirebaseService.getAll('eclairage-public-ensembles'),
        FirebaseService.getAll('eclairage-public-ensembles')
      ]);
      setEnsembles(ensemblesData);
      setSubcategories(subcategoriesData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file: File, field: string) => {
    if (!file) return;

    const ensemble = localFormData.newEnsemble || {};
    setUploading(prev => ({ ...prev, [field]: true }));

    try {
      const url = await FirebaseService.uploadFile(`eclairage-public-ensembles/${field}/${Date.now()}_${file.name}`, file);
      handleLocalInputChange('newEnsemble', { ...ensemble, [field]: url });
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Erreur lors du téléchargement du fichier');
    } finally {
      setUploading(prev => ({ ...prev, [field]: false }));
    }
  };

  const handleMultipleFileUpload = async (files: FileList, field: string) => {
    if (!files || files.length === 0) return;

    const ensemble = localFormData.newEnsemble || {};
    const existingImages = ensemble[field] || [];
    setUploading(prev => ({ ...prev, [field]: true }));

    try {
      const uploadPromises = Array.from(files).map(async (file, index) => {
        const url = await FirebaseService.uploadFile(`eclairage-public-ensembles/${field}/${Date.now()}_${index}_${file.name}`, file);
        return url;
      });

      const urls = await Promise.all(uploadPromises);
      handleLocalInputChange('newEnsemble', { ...ensemble, [field]: [...existingImages, ...urls] });
    } catch (error) {
      console.error('Error uploading files:', error);
      alert('Erreur lors du téléchargement des fichiers');
    } finally {
      setUploading(prev => ({ ...prev, [field]: false }));
    }
  };

  const handleAddEnsemble = () => {
    setEditingEnsemble(null);
    setShowForm(true);
    handleLocalInputChange('newEnsemble', {
      title: '',
      description: '',
      mainImage: '',
      additionalImages: [],
      products: [''],
      compositionImage: '',
      brochurePDF: '',
      catalogPDF: '',
      technicalSpecs: {},
      subcategoryId: subcategories.length > 0 ? subcategories[0].id : ''
    });
  };

  const handleEditEnsemble = (ensemble: Ensemble) => {
    setEditingEnsemble(ensemble);
    setShowForm(true);
    handleLocalInputChange('newEnsemble', ensemble);
  };

  const handleDeleteEnsemble = async (id: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet ensemble ?')) {
      try {
        await FirebaseService.delete('eclairage-public-ensembles', id);
        loadData();
      } catch (error) {
        console.error('Error deleting ensemble:', error);
      }
    }
  };

  const handleSaveEnsemble = async () => {
    try {
      const ensembleData = (localFormData.newEnsemble || {});
      
      if (editingEnsemble) {
        await FirebaseService.update('eclairage-public-ensembles', editingEnsemble.id, ensembleData);
      } else {
        await FirebaseService.create('eclairage-public-ensembles', ensembleData);
      }
      
      setShowForm(false);
      setEditingEnsemble(null);
      loadData();
    } catch (error) {
      console.error('Error saving ensemble:', error);
    }
  };

  const handleProductChange = (index: number, value: string) => {
    const products = [...(localFormData.newEnsemble || {}).products];
    products[index] = value;
    handleLocalInputChange('newEnsemble', { ...(localFormData.newEnsemble || {}), products });
  };

  const handleAddProduct = () => {
    const products = [...(localFormData.newEnsemble || {}).products, ''];
    handleLocalInputChange('newEnsemble', { ...(localFormData.newEnsemble || {}), products });
  };

  const handleRemoveProduct = (index: number) => {
    const products = (localFormData.newEnsemble || {}).products.filter((_: any, i: number) => i !== index);
    handleLocalInputChange('newEnsemble', { ...(localFormData.newEnsemble || {}), products });
  };

  const handleSpecChange = (index: number, field: 'key' | 'value', value: string) => {
    const specs = { ...(localFormData.newEnsemble || {}).technicalSpecs };
    const entries = Object.entries(specs);
    
    if (field === 'key') {
      const newEntries = entries.map(([k, v], i) => i === index ? [value, v] : [k, v]);
      handleLocalInputChange('newEnsemble', { ...(localFormData.newEnsemble || {}), technicalSpecs: Object.fromEntries(newEntries) });
    } else {
      const newEntries = entries.map(([k, v], i) => i === index ? [k, value] : [k, v]);
      handleLocalInputChange('newEnsemble', { ...(localFormData.newEnsemble || {}), technicalSpecs: Object.fromEntries(newEntries) });
    }
  };

  const handleAddSpec = () => {
    const specs = { ...(localFormData.newEnsemble || {}).technicalSpecs, '': '' };
    handleLocalInputChange('newEnsemble', { ...(localFormData.newEnsemble || {}), technicalSpecs: specs });
  };

  const handleRemoveSpec = (index: number) => {
    const entries = Object.entries((localFormData.newEnsemble || {}).technicalSpecs);
    const newEntries = entries.filter((_, i) => i !== index);
    handleLocalInputChange('newEnsemble', { ...(localFormData.newEnsemble || {}), technicalSpecs: Object.fromEntries(newEntries) });
  };

  if (loading) {
    return <LoadingSpinner>Chargement des données...</LoadingSpinner>;
  }

  return (
    <Container $isRTL={isRTL}>
      <SectionTitle $isRTL={isRTL}>Gestion Éclairage Public</SectionTitle>
      
      <TabContainer>
        <Tab 
          $isActive={activeTab === 'subcategories'} 
          $isRTL={isRTL}
          onClick={() => setActiveTab('subcategories')}
        >
          Sous-catégories
        </Tab>
        <Tab 
          $isActive={activeTab === 'ensembles'} 
          $isRTL={isRTL}
          onClick={() => setActiveTab('ensembles')}
        >
          Ensembles
        </Tab>
      </TabContainer>

      {activeTab === 'subcategories' ? (
        <AdminEclairagePublicSubcategories 
          t={t} 
          isRTL={isRTL} 
          formData={localFormData} 
          onInputChange={handleLocalInputChange} 
        />
      ) : (
        <>
          <ButtonGroup>
            <Button $isRTL={isRTL} $variant="primary" onClick={handleAddEnsemble}>
              Ajouter un ensemble
            </Button>
          </ButtonGroup>

          {showForm && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              style={{ marginTop: '2rem', background: '#f8fafc', padding: '2rem', borderRadius: '12px' }}
            >
              <SectionTitle $isRTL={isRTL}>
                {editingEnsemble ? 'Modifier l\'ensemble' : 'Nouvel ensemble'}
              </SectionTitle>
              
              <FormGroup $isRTL={isRTL}>
                <Label $isRTL={isRTL}>Sous-catégorie</Label>
                <Select
                  $isRTL={isRTL}
                  value={(localFormData.newEnsemble || {})?.subcategoryId || ''}
                  onChange={(e) => handleLocalInputChange('newEnsemble', { ...(localFormData.newEnsemble || {}), subcategoryId: e.target.value })}
                >
                  <option value="">Sélectionner une sous-catégorie</option>
                  {subcategories.map((subcategory) => (
                    <option key={subcategory.id} value={subcategory.id}>
                      {subcategory.name}
                    </option>
                  ))}
                </Select>
              </FormGroup>
              
              <FormGroup $isRTL={isRTL}>
                <Label $isRTL={isRTL}>Titre de l'ensemble</Label>
                <Input
                  $isRTL={isRTL}
                  type="text"
                  value={(localFormData.newEnsemble || {})?.title || ''}
                  onChange={(e) => handleLocalInputChange('newEnsemble', { ...(localFormData.newEnsemble || {}), title: e.target.value })}
                />
              </FormGroup>

              <FormGroup $isRTL={isRTL}>
                <Label $isRTL={isRTL}>Description</Label>
                <TextArea
                  $isRTL={isRTL}
                  value={(localFormData.newEnsemble || {})?.description || ''}
                  onChange={(e) => handleLocalInputChange('newEnsemble', { ...(localFormData.newEnsemble || {}), description: e.target.value })}
                />
              </FormGroup>

              <FormGroup $isRTL={isRTL}>
                <Label $isRTL={isRTL}>Image principale</Label>
                <Input
                  $isRTL={isRTL}
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileUpload(file, 'mainImage');
  }}
/>
                {uploading.mainImage && <p>Téléchargement...</p>}
              </FormGroup>

              <FormGroup $isRTL={isRTL}>
                <Label $isRTL={isRTL}>Images supplémentaires</Label>
                <Input
                  $isRTL={isRTL}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => {
                  const files = e.target.files;
                  if (files) handleMultipleFileUpload(files, 'additionalImages');
  }}
/>
                {uploading.additionalImages && <p>Téléchargement...</p>}
              </FormGroup>

              <ProductsList $isRTL={isRTL}>
                <Label $isRTL={isRTL}>Produits</Label>
                {(localFormData.newEnsemble || {})?.products?.map((product: string, index: number) => (
                  <ProductItem key={index} $isRTL={isRTL}>
                    <ProductInput
                      $isRTL={isRTL}
                      type="text"
                      value={product}
                      onChange={(e) => handleProductChange(index, e.target.value)}
                      placeholder="Nom du produit"
                    />
                    <RemoveButton onClick={() => handleRemoveProduct(index)}>
                      Supprimer
                    </RemoveButton>
                  </ProductItem>
                ))}
                <AddButton $isRTL={isRTL} onClick={handleAddProduct}>
                  Ajouter un produit
                </AddButton>
              </ProductsList>

              <FormGroup $isRTL={isRTL}>
                <Label $isRTL={isRTL}>Image de composition</Label>
                <Input
                  $isRTL={isRTL}
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileUpload(file, 'compositionImage');
  }}
/>
                {uploading.compositionImage && <p>Téléchargement...</p>}
              </FormGroup>

              <FormGroup $isRTL={isRTL}>
                <Label $isRTL={isRTL}>PDF Brochure</Label>
                <Input
                $isRTL={isRTL}
                type="file"
                accept=".pdf"
                onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFileUpload(file, 'brochurePDF');
  }}
/>
                {uploading.brochurePDF && <p>Téléchargement...</p>}
              </FormGroup>

              <FormGroup $isRTL={isRTL}>
                <Label $isRTL={isRTL}>PDF Catalogue</Label>
                <Input
                 $isRTL={isRTL}
                 type="file"
                 accept=".pdf"
                 onChange={(e) => {
                 const file = e.target.files?.[0];
                 if (file) handleFileUpload(file, 'catalogPDF');
  }}
/>
                {uploading.catalogPDF && <p>Téléchargement...</p>}
              </FormGroup>

              <SpecsSection $isRTL={isRTL}>
                <Label $isRTL={isRTL}>Caractéristiques techniques</Label>
                {Object.entries((localFormData.newEnsemble || {})?.technicalSpecs || {}).map(([key, value], index) => (
                  <SpecRow key={index}>
                    <SpecInput
                      $isRTL={isRTL}
                      type="text"
                      value={key}
                      onChange={(e) => handleSpecChange(index, 'key', e.target.value)}
                      placeholder="Caractéristique"
                    />
                    <SpecInput
                      $isRTL={isRTL}
                      type="text"
                      value={value as string}
                      onChange={(e) => handleSpecChange(index, 'value', e.target.value)}
                      placeholder="Valeur"
                    />
                    <RemoveButton onClick={() => handleRemoveSpec(index)}>
                      Supprimer
                    </RemoveButton>
                  </SpecRow>
                ))}
                <AddButton $isRTL={isRTL} onClick={handleAddSpec}>
                  Ajouter une caractéristique
                </AddButton>
              </SpecsSection>

              <ButtonGroup>
                <Button $isRTL={isRTL} $variant="primary" onClick={handleSaveEnsemble}>
                  {editingEnsemble ? 'Mettre à jour' : 'Créer'}
                </Button>
                <Button $isRTL={isRTL} $variant="secondary" onClick={() => setShowForm(false)}>
                  Annuler
                </Button>
              </ButtonGroup>
            </motion.div>
          )}

          <EnsemblesList>
            {ensembles.map((ensemble) => {
              const subcategory = subcategories.find(s => s.id === ensemble.subcategoryId);
              return (
                <EnsembleCard key={ensemble.id} $isRTL={isRTL}>
                  <EnsembleHeader>
                    <EnsembleTitle $isRTL={isRTL}>
                      {ensemble.title} {subcategory && `(${subcategory.name})`}
                    </EnsembleTitle>
                    <EnsembleActions>
                      <ActionButton $variant="edit" onClick={() => handleEditEnsemble(ensemble)}>
                        Modifier
                      </ActionButton>
                      <ActionButton $variant="delete" onClick={() => handleDeleteEnsemble(ensemble.id)}>
                        Supprimer
                      </ActionButton>
                    </EnsembleActions>
                  </EnsembleHeader>
                  <p>{ensemble.description}</p>
                  <div>
                    <strong>Sous-catégorie:</strong> {subcategory?.name || 'Non assignée'}
                  </div>
                  <div>
                    <strong>Produits:</strong> {ensemble.products.join(', ')}
                  </div>
                </EnsembleCard>
              );
            })}
          </EnsemblesList>
        </>
      )}
    </Container>
  );
};

export default AdminEclairagePublic;
